/** CodeMirror 6 editor wrapper with LaTeX syntax highlighting, autosave,
 *  light/dark theme, fullscreen toggle, and true RTL text direction.
 *
 *  CodeMirror renders lines as real DOM content, so the browser's native
 *  bidi algorithm handles RTL text correctly (unlike Monaco, which has no
 *  RTL support). Setting ``dir`` on the content DOM gives every line an
 *  RTL base direction; CSS keeps the lines anchored to the left edge.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { EditorView, keymap } from "@codemirror/view";
import { EditorSelection, Prec } from "@codemirror/state";
import { StreamLanguage, LanguageSupport } from "@codemirror/language";
import { stex } from "@codemirror/legacy-modes/mode/stex";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { oneDark } from "@codemirror/theme-one-dark";
import type { SaveStatus } from "../types";
import { useTheme } from "../hooks/ThemeContext";
import { SimpleTexEditor } from "./SimpleTexEditor";

interface Props {
  path: string;
  content: string;
  onContentChange: (content: string) => void;
  onSave: () => void;
  saveStatus: SaveStatus;
  onCursorChange?: (line: number, column: number) => void;
  targetLine?: { line: number; id: number } | null;
}

function languageExtension(path: string): LanguageSupport[] {
  const lower = path.toLowerCase();
  if (
    lower.endsWith(".tex") ||
    lower.endsWith(".sty") ||
    lower.endsWith(".cls") ||
    lower.endsWith(".bib")
  ) {
    return [new LanguageSupport(StreamLanguage.define(stex))];
  }
  if (lower.endsWith(".json")) return [json()];
  if (lower.endsWith(".md")) return [markdown()];
  return [];
}

export function CodeMirrorEditor({
  path,
  content,
  onContentChange,
  onSave,
  saveStatus,
  onCursorChange,
  targetLine,
}: Props): JSX.Element {
  const { theme } = useTheme();
  const cmRef = useRef<ReactCodeMirrorRef>(null);
  const [fullscreen, setFullscreen] = useState(false);
  // .tex-family files open in the lightweight plain-text editor; other
  // file types keep the CodeMirror source editor.
  const isTex = /\.(tex|sty|cls|bib)$/i.test(path);
  const [isRtl, setIsRtl] = useState(() => {
    try {
      const saved = localStorage.getItem("editor_direction");
      return saved ? saved === "rtl" : true;
    } catch {
      return true;
    }
  });

  const toggleRtl = () => {
    setIsRtl((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("editor_direction", next ? "rtl" : "ltr");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Keep latest callbacks in refs so extensions never go stale.
  const onSaveRef = useRef(onSave);
  const onCursorChangeRef = useRef(onCursorChange);
  useEffect(() => {
    onSaveRef.current = onSave;
    onCursorChangeRef.current = onCursorChange;
  }, [onSave, onCursorChange]);

  const extensions = useMemo(
    () => [
      ...languageExtension(path),
      EditorView.lineWrapping,
      // Per-line direction detection: Persian lines render RTL while
      // LaTeX/ASCII lines stay LTR. Forcing dir=rtl on the whole doc
      // scrambles mixed lines and breaks click hit-testing.
      isRtl
        ? EditorView.perLineTextDirection.of(true)
        : EditorView.contentAttributes.of({ dir: "ltr" }),
      Prec.highest(
        keymap.of([
          {
            key: "Mod-s",
            preventDefault: true,
            run: () => {
              onSaveRef.current();
              return true;
            },
          },
        ]),
      ),
      // Report cursor position changes for SyncTeX forward lookup.
      EditorView.updateListener.of((update) => {
        if (update.selectionSet) {
          const pos = update.state.selection.main.head;
          const line = update.state.doc.lineAt(pos);
          onCursorChangeRef.current?.(line.number, pos - line.from + 1);
        }
      }),
    ],
    [path, isRtl],
  );

  // Jump to line when triggered from web preview or error locator.
  useEffect(() => {
    const view = cmRef.current?.view;
    if (targetLine && view) {
      const lineNo = Math.min(Math.max(targetLine.line, 1), view.state.doc.lines);
      const line = view.state.doc.line(lineNo);
      view.dispatch({
        selection: EditorSelection.cursor(line.from),
        effects: EditorView.scrollIntoView(line.from, { y: "center" }),
      });
      view.focus();
    }
  }, [targetLine]);

  // Esc exits fullscreen.
  useEffect(() => {
    if (!fullscreen) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [fullscreen]);

  return (
    <div className={`editor-pane${fullscreen ? " fullscreen" : ""}`}>
      <div className="editor-tabs">
        {path && (
          <div className="editor-tab active">
            <span>{path.split("/").pop()}</span>
          </div>
        )}
        <span style={{ flex: 1 }} />
        {!isTex && (
          <button
            className="theme-toggle"
            title="تغییر جهت متن (RTL / LTR)"
            onClick={toggleRtl}
            style={{ padding: "2px 10px", fontSize: 12, marginRight: 6 }}
          >
            {isRtl ? "⇄ RTL (راست‌چین)" : "⇄ LTR (چپ‌چین)"}
          </button>
        )}
        <button
          className="theme-toggle"
          title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen editor"}
          onClick={() => setFullscreen((v) => !v)}
          style={{ padding: "2px 8px", fontSize: 12 }}
        >
          {fullscreen ? "⤓ Exit" : "⤢ Fullscreen"}
        </button>
      </div>
      <div
        className={`editor-container ${isRtl ? "rtl-editor" : "ltr-editor"}`}
        style={{ height: "100%", width: "100%" }}
      >
        {isTex ? (
          <SimpleTexEditor
            path={path}
            content={content}
            onContentChange={onContentChange}
            onSave={onSave}
            saveStatus={saveStatus}
            onCursorChange={onCursorChange}
            targetLine={targetLine}
          />
        ) : (
        <CodeMirror
          ref={cmRef}
          value={content}
          onChange={(value) => onContentChange(value)}
          extensions={extensions}
          theme={theme === "light" ? "light" : oneDark}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLine: true,
            highlightActiveLineGutter: true,
            autocompletion: false,
          }}
          style={{ height: "100%" }}
        />
        )}
      </div>
    </div>
  );
}
